import React, { useState, useEffect } from 'react';
import { Check, Star, Shield, Zap, TrendingUp } from 'lucide-react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import Loader from '../Common/Loader';

interface Stock {
  symbole: string;
  nom_societe: string;
  secteur?: string;
}

interface OnboardingFavoritesModalProps {
  onComplete: () => void;
}

const OnboardingFavoritesModal: React.FC<OnboardingFavoritesModalProps> = ({ onComplete }) => {
  const { token } = useAuth();
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStocks = async () => {
      try {
        const res = await fetch(`${API_URL}/v1/stocks/active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          // Sort by symbol
          setStocks(data.sort((a: Stock, b: Stock) => a.symbole.localeCompare(b.symbole)));
        }
      } catch (err) {
        console.error('Error fetching stocks:', err);
        setError('Impossible de charger les sociétés. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };

    fetchStocks();
  }, [token]);

  const toggleStock = (symbol: string) => {
    setSelected(prev => 
      prev.includes(symbol) 
        ? prev.filter(s => s !== symbol) 
        : [...prev, symbol]
    );
  };

  const handleSubmit = async () => {
    if (selected.length < 3) return;

    setSubmitting(true);
    try {
      // Register each favorite
      await Promise.all(selected.map(symbol => 
        fetch(`${API_URL}/api/favorites/${symbol}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        })
      ));
      
      onComplete();
    } catch (err) {
      console.error('Error saving favorites:', err);
      setError('Erreur lors de la sauvegarde. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loader fullScreen message="Préparation de votre radar..." />;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.9)',
      backdropFilter: 'blur(12px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px',
    }}>
      <div style={{
        backgroundColor: 'var(--bg-box)',
        borderRadius: '32px',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
        animation: 'modalAppear 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
      }}>
        <style>{`
          @keyframes modalAppear {
            from { opacity: 0; transform: scale(0.95) translateY(20px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          .stock-card {
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            cursor: pointer;
          }
          .stock-card:hover {
            transform: translateY(-4px);
            border-color: var(--navbar-accent-light) !important;
          }
          .stock-card.selected {
            background: linear-gradient(135deg, rgba(79, 70, 229, 0.1), rgba(124, 58, 237, 0.1));
            border-color: #6366f1 !important;
            box-shadow: 0 10px 20px -5px rgba(99, 102, 241, 0.2);
          }
        `}</style>

        {/* Header Section */}
        <div style={{
          padding: '40px 40px 30px',
          textAlign: 'center',
          background: 'linear-gradient(to bottom, rgba(79, 70, 229, 0.05), transparent)',
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: 'rgba(79, 70, 229, 0.1)',
            color: '#6366f1',
            padding: '8px 16px',
            borderRadius: '100px',
            fontSize: '13px',
            fontWeight: '700',
            marginBottom: '20px',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            <Zap size={14} fill="#6366f1" /> Configuration Initiale
          </div>
          <h1 style={{
            fontSize: '32px',
            fontWeight: '800',
            color: 'var(--text-heading)',
            margin: '0 0 12px',
            letterSpacing: '-0.5px'
          }}>
            Sélectionnez vos actions à surveiller
          </h1>
          <p style={{
            fontSize: '16px',
            color: 'var(--text-muted)',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: '1.6'
          }}>
            Nous surveillons le marché pour vous. Sélectionnez au moins <strong>3 sociétés</strong> pour recevoir vos premières alertes techniques et signaux de valorisation dès demain.
          </p>
        </div>

        {/* Selection Area */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '0 40px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: '16px',
          paddingBottom: '40px'
        }}>
          {stocks.map(stock => (
            <div
              key={stock.symbole}
              className={`stock-card ${selected.includes(stock.symbole) ? 'selected' : ''}`}
              onClick={() => toggleStock(stock.symbole)}
              style={{
                padding: '20px',
                borderRadius: '20px',
                border: '2px solid var(--border-color)',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                background: 'var(--bg-box-light)',
              }}
            >
              {selected.includes(stock.symbole) && (
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  backgroundColor: '#6366f1',
                  color: 'white',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 10px rgba(99, 102, 241, 0.4)',
                  zIndex: 2
                }}>
                  <Check size={14} strokeWidth={3} />
                </div>
              )}
              
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '16px',
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '12px',
                border: '1px solid var(--border-color)',
                overflow: 'hidden',
                padding: '8px'
              }}>
                <img 
                  src={`/images/societes/${stock.symbole}.png`} 
                  alt={stock.symbole}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.style.backgroundColor = 'var(--bg-hover)';
                      const fallback = document.createElement('div');
                      fallback.innerText = stock.symbole.substring(0, 2);
                      fallback.style.fontSize = '20px';
                      fallback.style.fontWeight = '800';
                      fallback.style.color = 'var(--text-heading)';
                      parent.appendChild(fallback);
                    }
                  }}
                />
              </div>
              
              <div style={{ fontWeight: '700', fontSize: '15px', color: 'var(--text-heading)', marginBottom: '4px' }}>
                {stock.symbole}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', height: '36px' }}>
                {stock.nom_societe}
              </div>
            </div>
          ))}
        </div>

        {/* Footer Actions */}
        <div style={{
          padding: '30px 40px',
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--bg-box)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '48px',
              height: '8px',
              backgroundColor: 'var(--bg-hover)',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${Math.min((selected.length / 3) * 100, 100)}%`,
                height: '100%',
                backgroundColor: selected.length >= 3 ? '#22c55e' : '#6366f1',
                transition: 'width 0.3s ease'
              }}></div>
            </div>
            <span style={{ fontSize: '14px', fontWeight: '600', color: selected.length >= 3 ? '#22c55e' : 'var(--text-muted)' }}>
              {selected.length < 3 
                ? `${selected.length} / 3 sélectionnés` 
                : `${selected.length} sociétés sélectionnées !`
              }
            </span>
          </div>

          <button
            onClick={handleSubmit}
            disabled={selected.length < 3 || submitting}
            style={{
              padding: '16px 32px',
              borderRadius: '16px',
              backgroundColor: selected.length >= 3 ? '#6366f1' : 'var(--bg-hover)',
              color: selected.length >= 3 ? 'white' : 'var(--text-muted)',
              fontSize: '16px',
              fontWeight: '700',
              border: 'none',
              cursor: selected.length >= 3 ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              transition: 'all 0.2s',
              boxShadow: selected.length >= 3 ? '0 10px 20px -5px rgba(99, 102, 241, 0.4)' : 'none',
            }}
          >
            {submitting ? 'Activation...' : 'Activer mon radar'}
            <TrendingUp size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFavoritesModal;
